import React from 'react'
import { FiArrowRight } from '@react-icons/all-files/fi/FiArrowRight'
import { FiCheckCircle } from '@react-icons/all-files/fi/FiCheckCircle'
import { FiDisc } from '@react-icons/all-files/fi/FiDisc'
import { FiGrid } from '@react-icons/all-files/fi/FiGrid'
import { FiLayers } from '@react-icons/all-files/fi/FiLayers'
import { FiMail } from '@react-icons/all-files/fi/FiMail'
import { FiMic } from '@react-icons/all-files/fi/FiMic'
import { FiMonitor } from '@react-icons/all-files/fi/FiMonitor'
import { FiPhone } from '@react-icons/all-files/fi/FiPhone'
import { FiUsers } from '@react-icons/all-files/fi/FiUsers'
import { FiVideo } from '@react-icons/all-files/fi/FiVideo'
import { FiVolume2 } from '@react-icons/all-files/fi/FiVolume2'
import { FiZap } from '@react-icons/all-files/fi/FiZap'
import BusinessInfo from '../../BusinessInfo'
import Action from '../primitives/Action'
import Container from '../foundations/Container'
import PageShell from '../foundations/PageShell'
import ClosingActionBand from './ClosingActionBand'
import FAQGroup from './FAQGroup'
import MarketCoverageNotice from './MarketCoverageNotice'
import MediaFeature from './MediaFeature'
import ServiceHero from './ServiceHero'
import styles from './OrlandoLocationHub.module.scss'

const categoryPages = [
  {
    title: 'Speaker Rental',
    description: 'Compare speakers, sound packages and delivery options for meetings, parties and live events.',
    href: '/orlando/speaker-rental',
    icon: FiVolume2
  },
  {
    title: 'DJ Equipment Rental',
    description: 'Browse professional DJ systems, players, mixers and event-ready equipment packages.',
    href: '/orlando/dj-equipment-rental',
    icon: FiDisc
  },
  {
    title: 'Karaoke Rental',
    description: 'Choose karaoke packages with microphones, lyrics displays and sound for your guest count.',
    href: '/orlando/karaoke-rental',
    icon: FiMic
  },
  {
    title: 'Projector Rental',
    description: 'Find projectors, screens and presentation packages for meetings, screenings and events.',
    href: '/orlando/projector-rental',
    icon: FiVideo
  },
  {
    title: 'TV Rental',
    description: 'Compare display sizes, stands and delivery for presentations, exhibits and watch parties.',
    href: '/orlando/tv-rental',
    icon: FiMonitor
  },
  {
    title: 'Stage Rental',
    description: 'Plan modular stage platforms and risers by footprint, height, surface and site access.',
    href: '/orlando/stage-rental',
    icon: FiLayers
  },
  {
    title: 'LED Video Wall Rental',
    description: 'Configure an LED video wall for conferences, exhibits, broadcasts and branded events.',
    href: '/orlando/led-video-wall-rental',
    icon: FiGrid
  }
]

const catalogLinks = [
  { label: 'Microphones', href: '/microphone-rental' },
  { label: 'Lighting', href: '/lighting-rental' },
  { label: 'Special effects', href: '/special-effects' }
]

const questions = [
  {
    question: 'Which areas does the Orlando branch serve?',
    answer: 'Our Orlando team serves Orlando, Tampa and Central Florida. Delivery availability and final labor pricing depend on the event address, dates, access and selected equipment.'
  },
  {
    question: 'Can I rent equipment online from the Orlando inventory?',
    answer: 'Yes. Choose a category to compare the equipment available for the Orlando storefront, enter your dates and location, and review pickup or delivery options for eligible products.'
  },
  {
    question: 'Can your Orlando team provide delivery, setup and event support?',
    answer: 'Yes. Delivery and setup are available for eligible orders, and managed production support can be quoted for events that need planning, technical labor or show-day operation.'
  },
  {
    question: 'Do you offer party tent rental in Orlando?',
    answer: 'No. Party tent rental is not currently offered by our Orlando branch. The Orlando team focuses on audiovisual equipment, staging and event-production support.'
  }
]

const CategoryCard = ({ category }) => {
  const Icon = category.icon

  return (
    <article className={styles.categoryCard}>
      <span className={styles.categoryIcon} aria-hidden='true'><Icon /></span>
      <h3>{category.title}</h3>
      <p>{category.description}</p>
      <Action
        href={category.href}
        icon={FiArrowRight}
        size='compact'
        variant='plain'
        className={styles.cardAction}
        dataConversionKey={`orlando.categories.${category.href.split('/').pop()}`}
        dataConversionLocation='category-grid'
        dataConversionChannel='rental'
        dataConversionSalesPath='self_service'
      >
        View {category.title}
      </Action>
    </article>
  )
}

const OrlandoLocationHub = ({ onQuote }) => {
  const browseAction = {
    label: 'Browse Orlando Equipment',
    href: '#orlando-rental-categories',
    icon: FiGrid,
    conversionKey: 'orlando.hero.catalog',
    channel: 'rental',
    salesPath: 'self_service',
    focusTargetOnNavigate: true
  }
  const quoteAction = {
    label: 'Request an Orlando Production Quote',
    icon: FiMail,
    conversionKey: 'orlando.hero.quote',
    channel: 'quote',
    salesPath: 'managed',
    onClick: onQuote
  }
  const callAction = {
    label: 'Call 407 807 0052',
    href: 'tel:+14078070052',
    external: true,
    icon: FiPhone,
    conversionKey: 'orlando.hero.call',
    channel: 'phone',
    salesPath: 'managed'
  }

  return (
    <PageShell className={styles.page}>
      <ServiceHero
        breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Orlando' }]}
        density='compact'
        eyebrow='Orlando branch · Serving Orlando, Tampa & Central Florida'
        title='Orlando AV Equipment Rental & Event Production'
        description='Rent professional AV equipment from our Orlando inventory or work with our team on delivery, setup and managed production across Central Florida.'
        image={{
          src: '/Pictures/categories-v2/orlando-hero-hybrid-v2.jpg',
          position: '68% 50%'
        }}
        imageAlt='Conference ballroom with a large LED video wall and classroom seating'
        primaryAction={browseAction}
        secondaryAction={quoteAction}
        tertiaryAction={callAction}
        stackActionsOnMobile
      />

      <MarketCoverageNotice variant='branch' content={{
        ariaLabel: 'Orlando branch and Central Florida service area',
        heading: 'Orlando inventory. Central Florida delivery.',
        body: 'Shop branch-specific equipment or plan delivery and setup across Orlando, Tampa and Central Florida.',
        cta: { label: 'Orlando branch details', url: '#orlando-branch' },
        conversionKey: 'orlando.market-coverage.branch',
        conversionLocation: 'after-hero'
      }} />

      <section className={styles.paths} aria-labelledby='orlando-paths-heading'>
        <Container width='wide'>
          <header className={styles.sectionHeader}>
            <span>Choose the right path</span>
            <h2 id='orlando-paths-heading'>Rent online or plan a managed production</h2>
            <p>Start with the path that matches the work you need. You can move between them without losing the Orlando storefront.</p>
          </header>
          <div className={styles.pathGrid}>
            <article className={`${styles.pathCard} ${styles.pathCardDark}`}>
              <span className={styles.pathIcon} aria-hidden='true'><FiGrid /></span>
              <div>
                <span className={styles.pathLabel}>Self-service rental</span>
                <h3>Compare Orlando equipment and pricing</h3>
                <p>Choose a category, set dates and location, then review eligible pickup or delivery options.</p>
              </div>
              <Action
                href={browseAction.href}
                icon={browseAction.icon}
                size='large'
                focusTargetOnNavigate
                dataConversionKey='orlando.path-choice.catalog'
                dataConversionLocation='path-choice'
                dataConversionChannel='rental'
                dataConversionSalesPath='self_service'
              >
                Browse Orlando Equipment
              </Action>
            </article>
            <article className={styles.pathCard}>
              <span className={styles.pathIcon} aria-hidden='true'><FiUsers /></span>
              <div>
                <span className={styles.pathLabel}>Managed production</span>
                <h3>Need planning, setup or show-day support?</h3>
                <p>Tell us about the venue, audience, schedule and production scope. An AV specialist can help build the plan.</p>
              </div>
              <Action
                icon={FiMail}
                onClick={onQuote}
                size='large'
                variant='outline'
                dataConversionKey='orlando.path-choice.quote'
                dataConversionLocation='path-choice'
                dataConversionChannel='quote'
                dataConversionSalesPath='managed'
              >
                Request a Production Quote
              </Action>
            </article>
          </div>
        </Container>
      </section>

      <section className={styles.categories} id='orlando-rental-categories' aria-labelledby='orlando-categories-heading'>
        <Container width='wide'>
          <header className={styles.sectionHeader}>
            <span>Dedicated Orlando rental pages</span>
            <h2 id='orlando-categories-heading' data-section-focus-target>Choose the equipment your event needs</h2>
            <p>Each page below is built around Orlando inventory, category-specific decisions and the right booking path.</p>
          </header>
          <div className={styles.categoryGrid}>
            {categoryPages.map(category => <CategoryCard category={category} key={category.href} />)}
          </div>
        </Container>
      </section>

      <section className={styles.additional} aria-labelledby='orlando-additional-heading'>
        <Container width='wide' className={styles.additionalGrid}>
          <div>
            <span className={styles.additionalEyebrow}>More Orlando inventory</span>
            <h2 id='orlando-additional-heading'>Use the shared catalog for additional AV categories</h2>
            <p>These pages display the catalog for your selected Orlando storefront. Availability and fulfillment details are confirmed before booking.</p>
            <div className={styles.catalogLinks}>
              {catalogLinks.map(link => (
                <Action
                  href={link.href}
                  icon={FiArrowRight}
                  key={link.href}
                  variant='plain'
                  dataConversionKey={`orlando.additional.${link.href.slice(1).replaceAll('/', '-')}`}
                  dataConversionLocation='additional-catalog'
                  dataConversionChannel='rental'
                  dataConversionSalesPath='self_service'
                >
                  {link.label}
                </Action>
              ))}
            </div>
          </div>
          <aside className={styles.notOffered} aria-label='Service not offered in Orlando'>
            <FiZap aria-hidden='true' />
            <div>
              <strong>Party tent rental is not offered in Orlando</strong>
              <p>Our Orlando branch focuses on audiovisual equipment, modular staging and event-production support.</p>
            </div>
          </aside>
        </Container>
      </section>

      <MediaFeature
        eyebrow='Orlando branch capability'
        heading='A local team for equipment, delivery and production support'
        body={(
          <>
            <p>Our Orlando warehouse supports equipment rentals and managed events across Central Florida. The team can coordinate delivery, setup and technical labor when the event requires more than a counter pickup.</p>
            <ul className={styles.proofList}>
              <li><FiCheckCircle aria-hidden='true' /> Orlando-based inventory and branch support</li>
              <li><FiCheckCircle aria-hidden='true' /> Delivery and setup across Orlando, Tampa and Central Florida</li>
              <li><FiCheckCircle aria-hidden='true' /> Managed AV planning for meetings, exhibits and live programs</li>
            </ul>
          </>
        )}
        image='/Pictures/orlando-partnerships.jpg'
        imageAlt='Audiovisual production setup supported by the Orlando team'
        imagePosition='center'
        tone='dark'
      />

      <div id='orlando-branch' className={styles.branchAnchor}>
        <BusinessInfo orlando ui='v2' sectionId='orlando-branch-heading' />
      </div>

      <FAQGroup
        questions={questions}
        title='Orlando AV rental questions'
        headingId='orlando-faq-heading'
        eyebrow='Central Florida service'
        intro='Answers about the Orlando storefront, delivery, production support and services that are not currently offered.'
      />

      <ClosingActionBand
        ariaLabel='Orlando rental and production options'
        eyebrow='Plan your Orlando event'
        heading='Choose equipment online or talk with an AV specialist'
        headingId='orlando-closing-action-heading'
        description='Start with the Orlando catalog for self-service rental, or request a quote when your event needs planning, delivery, setup or show-day support.'
        proofItems={['Orlando branch inventory', 'Central Florida delivery', 'Managed production support']}
        primaryAction={quoteAction}
        secondaryAction={browseAction}
      />
    </PageShell>
  )
}

export default OrlandoLocationHub
